import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { Class } from 'src/app/core/models/class.model';
import { ActivatedRoute, Router } from '@angular/router';
import { CrudService } from 'src/app/core/services/data/crud.service';
import { LoaderMessagingService } from 'src/app/core/services/messaging/loader-messaging.service';
import { ErrorHandlerService } from 'src/app/core/services/util/error-handler.service';
import { HeaderMessagingService } from 'src/app/core/services/messaging/header-messaging.service';
import { DataCustomizerService } from 'src/app/core/services/util/data-customizer.service';
import { CLASS } from 'src/app/core/constants/configuration/class-constants.config';
import { VIEW_MODULE_NAME } from 'src/app/core/constants/configuration/common.constant';
import { allClassesEndpoint, getClassSubjectsEndpoint, allGradeLevelsEndpoint, getActivePeriodStatusEndpoint, getStatusEndpoint, allHqtEndpoint, getClassOrderedCount, getActiveSchoolYearEndpoint, getProcessedOrderedCount } from 'src/app/core/constants/endpoints.constant';
import { takeUntil } from 'rxjs/operators';
import { Hqt } from 'src/app/core/models/hqt.model';
import { CURRICULUMS_PATH, CLASSES_PATH, CLASS_UPDATE_PATH } from 'src/app/core/constants/routes.constant';
import { FabButtonMessagingService } from 'src/app/core/services/messaging/fab-button-messaging.service';
import { UserService } from 'src/app/core/services/util/user.service';
import { homeRoutingPermissions } from 'src/app/core/constants/configuration/role-constants.config';

@Component({
	selector: 'app-class-view',
	templateUrl: './class-view.component.html',
	styleUrls: ['./class-view.component.scss']
})
export class ClassViewComponent implements OnInit, OnDestroy {

	unsubscribe: Subject<any> = new Subject();
	clazz: Class;
	reference: any = {};
	referenceValues: any = {};
	hasInitialized: boolean = false;
	curriculum: any = {};
	hqt: any = null;
	classOrderedCount : number = null;
	classProcessedOrderedCount : number = null;

  constructor(
		private route: ActivatedRoute,
		private router: Router,
		private crudService: CrudService,
		private loaderMessagingService: LoaderMessagingService,
		private errorHandlerService: ErrorHandlerService,
		private headerMessagingService: HeaderMessagingService,
		private dataCustomizerService: DataCustomizerService,
		private fabButtonMessagingService: FabButtonMessagingService,
    private userService: UserService,
	) {
		this.userService.checkRolePermission(CLASSES_PATH, homeRoutingPermissions);
		this.headerMessagingService.setHeader(CLASS, VIEW_MODULE_NAME, false, null);
	}

  	ngOnInit() {
		this.loaderMessagingService.showPageLoader(true);
		this.findClass(this.route.snapshot.paramMap.get('id'));
	}

	ngOnDestroy(): void {
		this.fabButtonMessagingService.sendMessage({ canEdit: false, path: null, id: null });
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}

	handleCompletion = (): void => {
		this.loaderMessagingService.showPageLoader(false);
	};

	get canView(): boolean {
		return this.clazz && this.hasInitialized;
	}

	onLinkClick(id: number): void {
		this.router.navigate([CURRICULUMS_PATH, id]);
	}

	private setReferenceValues(): void {

		const subject: string = this.reference.subjects.find(subj => this.clazz.subject === subj.code).name;
		const fromGradeLevel: string = this.reference.gradeLevels.find(level => this.clazz.fromGradeLevel === level.code).name;
		const toGradeLevel: string = this.reference.gradeLevels.find(level => this.clazz.toGradeLevel === level.code).name;
		const activePeriodType: string = this.reference.periodTypes.find(type => this.clazz.activePeriodType === type.code).name;
		const status: string = this.reference.status.find(status => this.clazz.status === status.code).name;

		this.referenceValues = { subject, fromGradeLevel, toGradeLevel, activePeriodType, curriculum: this.curriculum, status, hqt: this.hqt };
	}

	// PUT ALL API CALL BELOW
	private findClass(id: string): void {

		this.crudService
			.getById<Class>(allClassesEndpoint.concat(`/${id}`))
			.pipe(takeUntil(this.unsubscribe))
			.subscribe(response => {
				if (response) {
					this.clazz = this.dataCustomizerService.formatClassDetailsDisplay(response);
					this.curriculum = {
						id: response.classCurriculum.id,
						code: response.classCurriculum.code,
						name: response.classCurriculum.name,
						syCode: response.classCurriculum.syCode,
					};
					this.getClassOrderedCount();
				}
			}, this.errorHandlerService.handleError);
	}

	private getClassOrderedCount(): void {
		this.crudService
            .getById<any>(getClassOrderedCount + "?classCode=" + this.clazz.code)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((response) => {
				if(response) {
					this.classOrderedCount = response;
				}
				this.getClassProcessedOrderedCount();
        }, this.errorHandlerService.handleError);
	}

	private getClassProcessedOrderedCount(): void {
		this.crudService
            .getById<any>(getProcessedOrderedCount + "?classCode=" + this.clazz.code)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((response) => {
				if(response) {
					this.classProcessedOrderedCount = response;
				}
				this.setReferenceSubjects();
        }, this.errorHandlerService.handleError);
	}

	private setReferenceSubjects(): void {

		this.crudService
			.getById<any>(getClassSubjectsEndpoint)
			.pipe(takeUntil(this.unsubscribe))
			.subscribe(response => {

				if (response) {
					this.reference = {};
					this.reference.subjects = response;
					this.reference.subjects.sort((a, b) => a.code.localeCompare(b.code));
					this.setReferenceGradeLevels();
				}

			}, this.errorHandlerService.handleError);

	}

	private setReferenceGradeLevels(): void {

		this.crudService
			.getById<any>(allGradeLevelsEndpoint)
			.pipe(takeUntil(this.unsubscribe))
			.subscribe(response => {

				if (response) {
					this.reference.gradeLevels = response;
					this.reference.gradeLevels.sort((a, b) => a.code.localeCompare(b.code));
					this.setReferencePeriodTypes();
				}

			}, this.errorHandlerService.handleError);

	}

	private setReferencePeriodTypes(): void {

		this.crudService
			.getById<any>(getActivePeriodStatusEndpoint)
			.pipe(takeUntil(this.unsubscribe))
			.subscribe(response => {

				if (response) {
					this.reference.periodTypes = response;
					this.reference.periodTypes.sort((a, b) => a.code.localeCompare(b.code));
					this.setReferenceStatus();
				}

			}, this.errorHandlerService.handleError);

	}

	private setReferenceStatus(): void {

		this.crudService
			.getById<any>(getStatusEndpoint)
			.pipe(takeUntil(this.unsubscribe))
			.subscribe(response => {

				if (response) {
					this.reference.status = response;
					this.reference.status.sort((a, b) => a.code.localeCompare(b.code));
					if (this.clazz.hqtUsername && this.clazz.hqtUsername.trim() !== "") {
						this.findHqt();
					} else {
						this.setReferenceValues();
						this.handleCompletion();
					}
					this.checkActiveSchoolYear();
				}
				
			}, this.errorHandlerService.handleError);

	}

	private findHqt(): void {

		this.crudService
			.getAllBy<Hqt>(`${allHqtEndpoint}/get-by-name-or-email?keyword=${this.clazz.hqtUsername}`)
			.pipe(takeUntil(this.unsubscribe))
			.subscribe(response => {

				if (response) {
					this.hqt = response.map(hqt => ({ email: hqt.email, name: hqt.name }))[0];
					this.setReferenceValues();
				}

			}, this.errorHandlerService.handleError, this.handleCompletion);

	}

	private initFabMessage(): void {
		let roles = homeRoutingPermissions.find(route => route.path === CLASSES_PATH);
		const message: any = { canEdit: this.userService.hasRole(roles ? roles.roles : []), path: CLASS_UPDATE_PATH, id: this.clazz.id };
		this.fabButtonMessagingService.sendMessage(message);
		this.hasInitialized = true;
	}

	private checkActiveSchoolYear() {
		this.crudService
		.getById<any>(getActiveSchoolYearEndpoint)
		.pipe(takeUntil(this.unsubscribe))
		.subscribe(response => {
			if (response) {
				if(this.curriculum.syCode >= response.schoolYear) {
					this.initFabMessage();
				} else {
					this.hasInitialized = true;
				}
			}
		}, this.errorHandlerService.handleError);
	}

}
